import { STATIC_TABLE_SETTINGS } from 'store/reducers/constants/constants.js';
import { normalizeTime, formatDateMDY } from 'functions/general/dateFormat.js';

/**
 * Validates the date and time columns for pickup and dropoff in a data row based on table and general settings.
 *
 * This function checks if the time windows are enabled for the given table, extracts the relevant date and time values
 * for pickup and dropoff (exact, start, end), and validates that the time ranges are logical (e.g., start is before end).
 * It returns an object indicating whether the input is valid and any validation messages.
 *
 * @param {Object} state - The Redux state containing general and table-specific settings.
 * @param {string} tableName - The name of the table to validate against.
 * @param {Object} dataRow - The row of data containing date and time fields.
 * @param {string[]} [validationMessages=[]] - Array of validation messages to append to.
 * @param {boolean} [isValidInput=true] - Initial validity state of the input.
 * @returns {{ valid: boolean, messages: string[] }} An object with the validation result and messages.
 */
export const validateDateTimeColumns = (
  state,
  tableName,
  dataRow,
  validationMessages = [],
  isValidInput = true
) => {
  const timeWindowFields = STATIC_TABLE_SETTINGS[tableName] && STATIC_TABLE_SETTINGS[tableName]['timeWindows'];
  const timeWindowSettings = state['generalSettings']['time'] && state['generalSettings']['time'][tableName];
  let isValid = isValidInput;
  let messages = [...validationMessages];
  if (!timeWindowFields) { return {valid: isValid, messages: messages}}
  if (!timeWindowSettings) { return {valid: isValid, messages: messages}}
  else if (!timeWindowSettings.useTimeWindows) { return {valid: isValid, messages: messages}}
  else {
    let minPickupTime;
    let minPickupDate;
    let maxPickupTime;
    let maxpickupDate;
    let minDropoffTime;
    let minDropoffDate;
    let maxDropoffTime;
    let maxDropoffDate;
    if (timeWindowSettings.usePickupExact) {
      const pickupOutput = getDateAndTimeFromRow(state, dataRow, tableName, true, 'exact');
      minPickupTime = pickupOutput.time
      minPickupDate = pickupOutput.date
      maxPickupTime = pickupOutput.time
      maxpickupDate = pickupOutput.date
    }
    if (timeWindowSettings.usePickupStart) {
      const pickupOutput = getDateAndTimeFromRow(state, dataRow, tableName, true, 'start');
      minPickupTime = pickupOutput.time
      minPickupDate = pickupOutput.date
    }
    if (timeWindowSettings.usePickupEnd) {
      const pickupOutput = getDateAndTimeFromRow(state, dataRow, tableName, true, 'end');
      maxPickupTime = pickupOutput.time
      maxpickupDate = pickupOutput.date
    }
    if (timeWindowSettings.useDropoffExact) {
      const dropoffOutput = getDateAndTimeFromRow(state, dataRow, tableName, false, 'exact');
      minDropoffTime = dropoffOutput.time
      minDropoffDate = dropoffOutput.date
      maxDropoffTime = dropoffOutput.time
      maxDropoffDate = dropoffOutput.date
    }
    if (timeWindowSettings.useDropoffStart) {
      const dropoffOutput = getDateAndTimeFromRow(state, dataRow, tableName, false, 'start');
      minDropoffTime = dropoffOutput.time
      minDropoffDate = dropoffOutput.date
    }
    if (timeWindowSettings.useDropoffEnd) {
      const dropoffOutput = getDateAndTimeFromRow(state, dataRow, tableName, false, 'end');
      maxDropoffTime = dropoffOutput.time
      maxDropoffDate = dropoffOutput.date
    }
    
    let minPickup;
    let maxPickup;
    let minDropoff;
    let maxDropoff;
    if (state.generalSettings.time.timeUnit === 'clock') {
      if (minPickupTime || minPickupDate) {
        if (!minPickupDate || minPickupDate === 'Invalid Date' || minPickupDate === '') {
          minPickupDate = '1/1/1970';
        }
        let parsedMinTime = normalizeTime(minPickupTime ?? '12:00am');
        if (state.generalSettings.time.dayUnit === 'integer') {
          minPickupDate = formatDateMDY(minPickupDate);
        }
        minPickup = new Date(minPickupDate + ' ' + (parsedMinTime ?? ''));
      }

      if (maxPickupTime || maxpickupDate) {
        if (!maxpickupDate || maxpickupDate === 'Invalid Date' || maxpickupDate === '') {
          if (!minPickupDate || minPickupDate === 'Invalid Date' || minPickupDate === '') {
            maxpickupDate = '1/1/1970';
          }
          else {
            maxpickupDate = minPickupDate;
          }
        }
        let parsedMaxTime = normalizeTime(maxPickupTime ?? '11:59pm');
        if (state.generalSettings.time.dayUnit === 'integer') {
          maxpickupDate = formatDateMDY(maxpickupDate);
        }
        maxPickup = new Date(maxpickupDate + ' ' + (parsedMaxTime ?? ''));
      }

      if (minDropoffTime || minDropoffDate) {
        if (!minDropoffDate || minDropoffDate === 'Invalid Date' || minDropoffDate === '') {
          minDropoffDate = '1/1/1970';
        }
        let parsedMinDropoffTime = normalizeTime(minDropoffTime ?? '12:00am');
        if (state.generalSettings.time.dayUnit === 'integer') {
          minDropoffDate = formatDateMDY(minDropoffDate);
        }
        minDropoff = new Date(minDropoffDate + ' ' + (parsedMinDropoffTime ?? ''));
      }

      if (maxDropoffTime || maxDropoffDate) {
        if (!maxDropoffDate || maxDropoffDate === 'Invalid Date' || maxDropoffDate === '') {
          // Fall back to the epoch when there's no dropoff start date to borrow.
          // A row can have a dropoff *deadline* but no dropoff *start* (e.g. a
          // delivery-by time with an open start), in which case the min-dropoff
          // block above never runs and minDropoffDate is still undefined —
          // assigning it here would leave maxDropoffDate undefined and blow up on
          // the .toString() below. Mirrors the max-pickup handling.
          if (!minDropoffDate || minDropoffDate === 'Invalid Date' || minDropoffDate === '') {
            maxDropoffDate = '1/1/1970';
          }
          else {
            maxDropoffDate = minDropoffDate;
          }
        }
        let parsedMaxDropoffTime = normalizeTime(maxDropoffTime ?? '11:59pm');
        if (state.generalSettings.time.dayUnit === 'integer') {
          maxDropoffDate = formatDateMDY(maxDropoffDate);
        }
        maxDropoff = new Date(maxDropoffDate + ' ' + (parsedMaxDropoffTime ?? ''));
      } 
    }
    else {
      minPickup = minPickupTime;
      maxPickup = maxPickupTime;
      minDropoff = minDropoffTime;
      maxDropoff = maxDropoffTime;
    }

    if (minPickup && maxPickup && minPickup > maxPickup) {
      isValid = false;
      messages.push('Pickup start time must not exceed pickup end time.');
    }
    if (minDropoff && maxDropoff && minDropoff > maxDropoff) {
      isValid = false;
      messages.push('Dropoff start time must not exceed dropoff end time.');
    }

    if (minPickup && maxDropoff && (maxDropoff < minPickup)) {
      isValid = false;
      messages.push('Dropoff end time must not be before pickup start time.');
    }

  }
  return {valid: isValid, messages: messages}
}

const getDateAndTimeFromRow = (state, dataRow, tableName, isPickup, timeType) => {
  /**
   * Used to get the time from the time window fields; this will combined
   * the date and time fields into a single date object;
   * 
   * @param {object} state the current state
   * @param {string} tableName the name of the table
   * @param {boolean} isPickup whether this is a pickup or dropoff time
   * @param {string} timeType the type of time, should be one of 'start', 'end', 'exact'
   */
  if (timeType !== 'start' && timeType !== 'end' && timeType !== 'exact') {
    throw new Error('Invalid timeType input to getDateAndTime function: ' + timeType)
  }
  const timeSettings = STATIC_TABLE_SETTINGS[tableName]['timeWindows'];
  let time;
  let date;
  if (timeSettings) {
    if (state.generalSettings.time.timeUnit && state.generalSettings.time.timeUnit === 'clock') {
      if (isPickup && timeType === 'start') {
        if (timeSettings.pickupStart) {
          time = dataRow[timeSettings.pickupStart.columnNameTime]
        } else {
          throw new Error('Missing pickup start time field for table: ' + tableName)
        }
      }
      if (isPickup && timeType === 'end') {
        if (timeSettings.pickupEnd) {
          time = dataRow[timeSettings.pickupEnd.columnNameTime]
        } else {
          throw new Error('Missing pickup end time field for table: ' + tableName)
        }
      }
      if (isPickup && timeType === 'exact') {
        if (timeSettings.pickupExact) {
          time = dataRow[timeSettings.pickupExact.columnNameTime]
        } else {
          throw new Error('Missing pickup exact time field for table: ' + tableName)
        }
      }
      if (!isPickup && timeType === 'start') {
        if (timeSettings.dropoffStart) {
          time = dataRow[timeSettings.dropoffStart.columnNameTime]
        } else {
          throw new Error ('Missing dropoff start time field for table: ' + tableName)
        }
      }
      if (!isPickup && timeType === 'end') {
        if (timeSettings.dropoffEnd) {
          time = dataRow[timeSettings.dropoffEnd.columnNameTime]
        } else {
          throw new Error('Missing dropoff end time field for table: ' + tableName)
        }
      }
      if (!isPickup && timeType === 'exact') {
        if (timeSettings.dropoffExact) {
          time = dataRow[timeSettings.dropoffExact.columnNameTime]
        } else {
          throw new Error('Missing dropoff exact time field for table: ' + tableName)
        }
      }
    }

    if (state.generalSettings.time.timeUnit && state.generalSettings.time.timeUnit === 'integer') {
      if (isPickup && timeType === 'start') {
        if (timeSettings.pickupStart) {
          time = dataRow[timeSettings.pickupStart.columnNameInt]
        }
      }
      if (isPickup && timeType === 'end') {
        if (timeSettings.pickupEnd) {
          time = dataRow[timeSettings.pickupEnd.columnNameInt]
        }
      }
      if (isPickup && timeType === 'exact') {
        if (timeSettings.pickupExact) {
          time = dataRow[timeSettings.pickupExact.columnNameInt]
        }
      }
      if (!isPickup && timeType === 'start') {
        if (timeSettings.dropoffStart) {
          time = dataRow[timeSettings.dropoffStart.columnNameInt]
        }
      }
      if (!isPickup && timeType === 'end') {
        if (timeSettings.dropoffEnd) {
          time = dataRow[timeSettings.dropoffEnd.columnNameInt]
        }
      }
      if (!isPickup && timeType === 'exact') {
        if (timeSettings.dropoffExact) {
          time = dataRow[timeSettings.dropoffExact.columnNameInt]
        }
      }
    }

    if (state.generalSettings.time.dayUnit && state.generalSettings.time.dayUnit === 'integer') {

        if (isPickup && timeType === 'start') {
            if (timeSettings.pickupStart && timeSettings.pickupStart.columnNameDay) {
            const day = dataRow[timeSettings.pickupStart.columnNameDay];
            if (day) {
                date = new Date('1/1/1970');
                date.setDate(date.getDate() + day);
            }
            }
        }

        if (isPickup && timeType === 'end') {
            if (timeSettings.pickupEnd && timeSettings.pickupEnd.columnNameDay) {
            const day = dataRow[timeSettings.pickupEnd.columnNameDay]
            if (day) {
                date = new Date('1/1/1970');
                date.setDate(date.getDate() + day);
            }
            }
        }

        if (isPickup && timeType === 'exact') {
            if (timeSettings.pickupExact && timeSettings.pickupExact.columnNameDay) {
            const day = dataRow[timeSettings.pickupExact.columnNameDay];
            if (day) {
                date = new Date('1/1/1970');
                date.setDate(date.getDate() + day);
            }
            }
        }

        if (!isPickup && timeType === 'start') {
            if (timeSettings.dropoffStart && timeSettings.dropoffStart.columnNameDay) {
            const day = dataRow[timeSettings.dropoffStart.columnNameDay];
            if (day) {
                date = new Date('1/1/1970');
                date.setDate(date.getDate() + day);
            }
            }
        }

        if (!isPickup && timeType === 'end') {
            if (timeSettings.dropoffEnd && timeSettings.dropoffEnd.columnNameDay) {
            const day = dataRow[timeSettings.dropoffEnd.columnNameDay];
            if (day) {
                date = new Date('1/1/1970');
                date.setDate(date.getDate() + day);
            }
            }
        }

        if (!isPickup && timeType === 'exact') {
            if (timeSettings.dropoffExact && timeSettings.dropoffExact.columnNameDay) {
            const day = dataRow[timeSettings.dropoffExact.columnNameDay];
            if (day) {
                date = new Date('1/1/1970');
                date.setDate(date.getDate() + day);
            }
            }
        }
    }

  }

  return {date: date, time: time}
}
